import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../store.js';
import { vault } from '../sync/vault.js';
import { index } from '../worker/client.js';
import type { BacklinkHit, UnlinkedMention } from '../worker/api.js';
import { useIndexRev } from './hooks.js';
import { markMatches } from './highlight.jsx';

export function BacklinksPane({ noteId }: { noteId: string }) {
  const rev = useIndexRev();
  const openNote = useApp((s) => s.openNote);
  const [linked, setLinked] = useState<BacklinkHit[] | null>(null);
  const [unlinked, setUnlinked] = useState<UnlinkedMention[]>([]);
  const [showUnlinked, setShowUnlinked] = useState(false);

  // Reset when switching notes (declared before the loader so it runs first).
  useEffect(() => {
    setLinked(null);
    setUnlinked([]);
    setShowUnlinked(false);
  }, [noteId]);

  useEffect(() => {
    let stale = false;
    void index.backlinks(noteId).then((h) => {
      if (!stale) setLinked(h);
    });
    void index.unlinkedMentions(noteId).then((h) => {
      if (!stale) setUnlinked(h);
    });
    return () => {
      stale = true;
    };
  }, [noteId, rev]);

  const groups = useMemo(() => {
    const m = new Map<string, { title: string; hits: BacklinkHit[] }>();
    for (const h of linked ?? []) {
      let g = m.get(h.sourceId);
      if (!g) m.set(h.sourceId, (g = { title: h.sourceTitle, hits: [] }));
      g.hits.push(h);
    }
    return [...m.entries()];
  }, [linked]);

  /** Wrap the mention in [[...]] in the SOURCE note. Insert at `end` first so
   * the `start` offset stays valid; one transact = one undo/sync unit. */
  const linkMention = (hit: UnlinkedMention) => {
    const doc = vault.noteDoc(hit.sourceId);
    const yt = vault.ytext(hit.sourceId);
    if (!doc || !yt) return;
    // The offsets come from the index worker, which lags edits by a debounce.
    // Verify the source text still reads exactly `matched` at [start,end) so a
    // concurrent edit (remote, or a rename-rewrite) can't make us splice into
    // the wrong place and corrupt the note. Bail and let the index refresh.
    if (yt.toString().slice(hit.start, hit.end) !== hit.matched) {
      setUnlinked((prev) => prev.filter((h) => h !== hit));
      return;
    }
    doc.transact(() => {
      yt.insert(hit.end, ']]');
      yt.insert(hit.start, '[[');
    });
    // Optimistically fix up local state until the index catches up: drop this
    // hit and shift later hits in the same note by the 4 inserted chars.
    setUnlinked((prev) =>
      prev.flatMap((h) => {
        if (h.sourceId === hit.sourceId && h.start === hit.start && h.end === hit.end) return [];
        if (h.sourceId === hit.sourceId && h.start > hit.start) {
          return [{ ...h, start: h.start + 4, end: h.end + 4 }];
        }
        return [h];
      }),
    );
  };

  return (
    <div className="bl-pane">
      <section className="panel-section">
        <div className="panel-header">
          <span className="panel-title">Linked mentions</span>
          <span className="count-badge">{linked?.length ?? 0}</span>
        </div>
        {linked === null ? (
          <div className="spinner" />
        ) : groups.length === 0 ? (
          <div className="pane-empty">No other note links here yet.</div>
        ) : (
          groups.map(([sourceId, g]) => (
            <div className="bl-group" key={sourceId}>
              <button className="bl-source" onClick={() => openNote(sourceId)}>
                {g.title}
              </button>
              {g.hits.map((h, i) => (
                <button
                  key={`${h.start}:${i}`}
                  className="bl-snippet"
                  onClick={() => openNote(h.sourceId)}
                >
                  {h.snippet}
                </button>
              ))}
            </div>
          ))
        )}
      </section>

      <section className="panel-section">
        <button className="panel-header collapsible" onClick={() => setShowUnlinked(!showUnlinked)}>
          <span className={`tree-chevron${showUnlinked ? ' open' : ''}`}>▸</span>
          <span className="panel-title">Unlinked mentions</span>
          <span className="count-badge">{unlinked.length}</span>
        </button>
        {showUnlinked &&
          (unlinked.length === 0 ? (
            <div className="pane-empty">No unlinked mentions found.</div>
          ) : (
            unlinked.map((h, i) => (
              <div className="bl-unlinked" key={`${h.sourceId}:${h.start}:${i}`}>
                <button className="bl-snippet" onClick={() => openNote(h.sourceId)}>
                  <span className="bl-source-inline">{h.sourceTitle}</span>
                  {markMatches(h.snippet, [h.matched])}
                </button>
                <button
                  className="bl-linkbtn"
                  title={`Turn "${h.matched}" into a link`}
                  onClick={() => linkMention(h)}
                >
                  Link
                </button>
              </div>
            ))
          ))}
      </section>
    </div>
  );
}
